import { REACT_APP_API_BASE_V2 } from "Config/Secrets";
import Axios from "Services/Auth/AxiosInterceptors";

/**
 * @description Fetch visit notes PDFs for a given patient and visit
 * @param {Object} payload - { visitId: String, patientId: String }
 * @param {Number} page - Current page number
 * @param {Number} recordsPerPage - Items per page
 * @returns {Object} result from API response
 */
export const GetVisitPdfs = async (payload, page = 1, recordsPerPage = 10) => {
  const {
    data: { result },
  } = await Axios.post(`/admin/patient/visit/pdfs?page=${page}&perPage=${recordsPerPage}`, payload);
  return result;
};

/**
 * @description Securely view a PDF by pdfId
 * @param {String} pdfId - The _id of the PDF record
 * @returns {Promise<String>}
 */
export const ViewPdf = async (pdfId) => {
  const { data } = await Axios.get(`/admin/pdf/view/${pdfId}`);
  console.log(data?.result?.pdfUrl, "DAta");
  return data?.result?.pdfUrl;
};

/**
 * @description Invalidate a visit notes PDF
 * @param {String} pdfID -  pdfId
 * @returns {Object} result from API response
 */
/**
 * Invalidate a note. Always cancels a still-pending delivery, so a voided note that
 * has not been emailed yet never reaches the patient.
 * @param {String} pdfID
 * @param {String} [reason]
 */
export const InvalidateVisitPdf = async (pdfID, reason) => {
  try {
    const {
      data: { result },
    } = await Axios.post(
      `/pdf/admin/deactivate-note/${pdfID}`,
      reason ? { reason } : {},
      {
        baseURL: REACT_APP_API_BASE_V2,
      },
    );
    return result;
  } catch (err) {
    throw new Error(unwrapError(err));
  }
};

/**
 * The v2 API returns errors as `{ errors: [message] }`. The day-limit and
 * paid-visit rules surface here as plain sentences meant to be shown verbatim,
 * so the first entry is preferred over a generic fallback.
 */
const unwrapError = (err) =>
  err?.response?.data?.errors?.[0] || err?.message || "Oops Something Went Wrong";

/**
 * @description Visits the patient paid a doctor note for — the only ones a note
 * may be issued against. Powers the visit picker when creating a note.
 * @param {String} patientId
 * @returns {Promise<Array>} [{ visitId, createdAt, reason, isRush, hasNote, noteDates }]
 */
export const GetEligibleNoteVisits = async (patientId) => {
  try {
    const {
      data: { result },
    } = await Axios.get(`/pdf/admin/eligible-visits/${patientId}`, {
      baseURL: REACT_APP_API_BASE_V2,
    });
    return result || [];
  } catch (err) {
    throw new Error(unwrapError(err));
  }
};

/**
 * @description Days of leave used and remaining in the rolling 30-day window.
 * @param {String} patientId
 * @param {String} [excludePdfId] Omit one note from the tally — pass the note being
 * edited so the figure reflects what can be issued once it is replaced.
 * @returns {Promise<Object>} { daysUsed, daysRemaining, limit, windowDays, windowStart, windowEnd }
 */
export const GetNoteDayUsage = async (patientId, excludePdfId) => {
  try {
    const query = excludePdfId ? `?excludePdfId=${excludePdfId}` : "";
    const {
      data: { result },
    } = await Axios.get(`/pdf/admin/day-usage/${patientId}${query}`, {
      baseURL: REACT_APP_API_BASE_V2,
    });
    return result;
  } catch (err) {
    throw new Error(unwrapError(err));
  }
};

/**
 * @description Issue a doctor note as an admin. Sends immediately (rush).
 * @param {Object} payload - { visitId, content, startDate, endDate, returnDate, serviceDate, noteType, companies }
 * @returns {Promise<Object>} the created PDF record
 */
export const CreateDoctorNote = async (payload) => {
  try {
    const {
      data: { result },
    } = await Axios.post(`/pdf/admin/create-note`, payload, {
      baseURL: REACT_APP_API_BASE_V2,
    });
    return result;
  } catch (err) {
    throw new Error(unwrapError(err));
  }
};

/**
 * @description Edit a note: invalidates the existing one and issues a replacement.
 * The original's days are released first, so re-using the same dates does not
 * count twice against the 30-day limit.
 * @param {String} pdfId - the note being replaced
 * @param {Object} payload - same shape as CreateDoctorNote, minus visitId
 * @returns {Promise<Object>} { invalidatedPdfId, note }
 */
export const EditDoctorNote = async (pdfId, payload) => {
  try {
    const {
      data: { result },
    } = await Axios.post(`/pdf/admin/edit-note/${pdfId}`, payload, {
      baseURL: REACT_APP_API_BASE_V2,
    });
    return result;
  } catch (err) {
    throw new Error(unwrapError(err));
  }
};

/**
 * @description Pinned notes for a patient chart — staff-only internal context,
 * NOT part of the medical record. Admin has read access only; creating, editing
 * and archiving stay with the providers who own the chart.
 * @param {String} patientId
 * @param {Boolean} [includeArchived] include archived entries (history view)
 * @returns {Promise<Array>}
 */
export const GetPinnedNotes = async (patientId, includeArchived = true) => {
  try {
    const query = includeArchived ? "?includeArchived=true" : "";
    const {
      data: { result },
    } = await Axios.get(`/pinned-notes/${patientId}${query}`);
    return result || [];
  } catch (err) {
    throw new Error(unwrapError(err));
  }
};
